import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DavetliKatilimciService from "../../../services/srv-davetli/DavetliKatilimciService";
import LoadingPanel from "../../../components/sys-toolbox/LoadingPanel";
import CommonGrid from "../../../components/sys-common/CommonGrid";
import { guestParticipantGridColumns } from "./GuestParticipantGridColumns";
import { GuestParticipantQrDetail } from "./GuestParticipantQrDetail";
import { confirmAlert } from "react-confirm-alert";
import { getConfirmWarningMessage } from "../../../helpers/utils";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@progress/kendo-react-layout";

function GuestParticipantList(props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [hasPending, setHasPending] = useState(false);
  const eventId = props.event.davetlI_ETKINLIK_TANIM_ID;
  const [dataState, setDataState] = useState({
    take: 10,
    skip: 0,
    filter: {
      logic: "and",
      filters: [
        {
          field: "davetlI_ETKINLIK_TANIM_ID",
          operator: "eq",
          value: eventId,
        },
      ],
    },
    sort: [{ field: "crE_DATE", dir: "desc" }],
  });

  useEffect(() => {
    getParticipants();
  }, [dataState]);

  useEffect(() => {
    if (props.isAdded) {
      getParticipants();
      // Add/delete/send sonrası pending buton görünürlüğü bayat kalmasın.
      refreshPendingSummary();
      props.setIsAdded(false);
    }
  }, [props.isAdded]);

  useEffect(() => {
    refreshPendingSummary();
  }, [eventId]);

  function refreshPendingSummary() {
    if (!eventId) {
      setHasPending(false);
      return;
    }
    DavetliKatilimciService.getPendingNotificationSummary(eventId)
      .then((summary) => {
        setHasPending(!!summary?.hasPending);
      })
      .catch(() => {
        setHasPending(false);
      });
  }

  function getParticipants() {
    setLoading(true);
    DavetliKatilimciService.getList(dataState)
      .then((res) => {
        setData(res?.data);
      })
      .finally(() => setLoading(false));
  }

  function deleteParticipant(dataItem) {
    const defaultAlertMessages = {
      TittleWarning: t("warning"),
      MessageDetailWarning: t("are_you_sure_delete"),
      Yes: t("yes"),
      No: t("no"),
    };
    confirmAlert({
      customUI: ({ onClose }) => {
        return getConfirmWarningMessage(
          onClose,
          () => {
            DavetliKatilimciService.delete(
              dataItem.davetlI_KATILIMCI_ID,
            ).then(() => props.setIsAdded(true));
          },
          defaultAlertMessages,
        );
      },
    });
  }

  function sendNotificationToAll() {
    const defaultAlertMessages = {
      TittleWarning: t("warning"),
      MessageDetailWarning: t("are_you_sure_send_notification_to_all"),
      Yes: t("yes"),
      No: t("no"),
    };
    confirmAlert({
      customUI: ({ onClose }) => {
        return getConfirmWarningMessage(
          onClose,
          () => {
            setLoading(true);
            // KatilimciIds gönderilmez: backend etkinliğin tüm geçerli
            // katılımcılarını hedefler.
            DavetliKatilimciService.sendNotification({
              DAVETLI_ETKINLIK_TANIM_ID: eventId,
            })
              .then((res) => {
                if (!res) return;
                props.setIsAdded(true);
                refreshPendingSummary();
              })
              .catch(() => {})
              .finally(() => setLoading(false));
          },
          defaultAlertMessages,
        );
      },
    });
  }

  function sendPendingNotification() {
    const defaultAlertMessages = {
      TittleWarning: t("warning"),
      MessageDetailWarning: t("are_you_sure_send_pending_notification"),
      Yes: t("yes"),
      No: t("no"),
    };
    confirmAlert({
      customUI: ({ onClose }) => {
        return getConfirmWarningMessage(
          onClose,
          () => {
            setLoading(true);
            DavetliKatilimciService.sendNotification({
              DAVETLI_ETKINLIK_TANIM_ID: eventId,
              OnlyPendingChannels: true,
            })
              .then((res) => {
                if (!res) return;
                props.setIsAdded(true);
                refreshPendingSummary();
              })
              .catch(() => {})
              .finally(() => setLoading(false));
          },
          defaultAlertMessages,
        );
      },
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("participant_list")}</CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? <LoadingPanel /> : null}
        {detailItem ? (
          <GuestParticipantQrDetail
            dataItem={detailItem}
            onClose={() => setDetailItem(null)}
          />
        ) : null}
        <CommonGrid
          data={data}
          dataState={dataState}
          onDataStateChange={(e) => setDataState(e.dataState)}
          columns={guestParticipantGridColumns}
          addButton={{
            text: t("add_participant"),
            onClick: props.toggleParticipantAdd,
          }}
          detailButton={{
            text: t("detail"),
            onClick: (dataItem) => setDetailItem(dataItem),
          }}
          deleteButton={{
            text: t("delete"),
            onClick: deleteParticipant,
          }}
          exportExcelProps={{
            loadingFunction: setLoading,
            serviceMethod: DavetliKatilimciService.getForExcel,
            fileName: "DavetliKatilimciListesi",
          }}
          extraToolbarButtons={[
            {
              key: "send_notification",
              text: t("send_notification"),
              icon: "fa fa-bell",
              color: "info",
              onClick: sendNotificationToAll,
            },
            ...(hasPending
              ? [
                  {
                    key: "send_pending_notification",
                    text: t("send_pending_notification"),
                    icon: "fa fa-exclamation-triangle",
                    color: "warning",
                    onClick: sendPendingNotification,
                  },
                ]
              : []),
          ]}
          dataItemKey="davetlI_KATILIMCI_ID"
        />
      </CardBody>
    </Card>
  );
}

export { GuestParticipantList };
